import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { backupService } from '../../services/api';
import '../../styles/DatabaseBackups.css';

type Backup = {
  id: number;
  filename: string;
  file_size_bytes: number;
  backup_datetime: string;
  created_by: number;
  created_by_name: string;
  is_auto_backup: boolean;
  notes: string | null;
  status: string;
  restore_datetime: string | null;
  restored_by: number | null;
  restored_by_name: string | null;
  onedrive_file_id: string | null;
  is_uploaded_to_onedrive: boolean;
  local_file_path: string | null;
  local_file_exists: boolean;
};

type PaginationInfo = {
  total: number;
  page: number;
  limit: number;
  pages: number;
};

const DatabaseBackups: React.FC = () => {
  const navigate = useNavigate();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  });
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [selectedBackupId, setSelectedBackupId] = useState<number | null>(null);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreLoading, setRestoreLoading] = useState<boolean>(false);
  const [createBackupLoading, setCreateBackupLoading] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [overrideBackupTable, setOverrideBackupTable] = useState<boolean>(false);

  // Fetch backups from the API
  const fetchBackups = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = { 
        page: pagination.page, 
        limit: pagination.limit 
      };
      
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await backupService.getAll(params);
      
      if (response.data.success) {
        setBackups(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError(response.data.message || 'Failed to fetch backups');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch backups');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, startDate, endDate]);

  // Initial fetch of backups
  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  // Format file size to human-readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date to local date-time string
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // Handle date filter change
  const handleFilterChange = (e: React.FormEvent) => {
    e.preventDefault();
    fetchBackups();
  };

  // Handle backup creation
  const handleCreateBackup = async () => {
    setCreateBackupLoading(true);
    setError(null);
    
    try {
      const response = await backupService.create({ notes });
      
      if (response.data.success) {
        fetchBackups();
        setNotes('');
      } else {
        setError(response.data.message || 'Failed to create backup');
      }
    } catch (err: any) {
      console.error('Backup creation error:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create backup';
      const errorDetails = err.response?.data?.details;
      setError(errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage);
    } finally {
      setCreateBackupLoading(false);
    }
  };

  // Open restore confirmation modal
  const openRestoreModal = (backupId: number) => {
    setSelectedBackupId(backupId);
    setConfirmationText('');
    setRestoreError(null);
    setOverrideBackupTable(false);
    setConfirmationModalOpen(true);
  };

  // Handle backup restore
  const handleRestoreBackup = async () => {
    if (confirmationText !== 'Confirm') {
      setRestoreError('Please type "Confirm" to proceed with the restore');
      return;
    }
    
    if (!selectedBackupId) {
      setRestoreError('No backup selected');
      return;
    }
    
    setRestoreLoading(true);
    setRestoreError(null);
    
    try {
      const response = await backupService.restore(selectedBackupId, confirmationText, overrideBackupTable);
      
      if (response.data.success) {
        // Show success message
        alert('✅ Database restored successfully!\n\nThe database has been restored from the backup. You may need to refresh the page to see the changes.');
        
        setConfirmationModalOpen(false);
        setSelectedBackupId(null);
        setConfirmationText('');
        setOverrideBackupTable(false);
        
        // Refresh the backup list
        fetchBackups();
        
        // Optionally reload the page after a short delay to ensure all data is fresh
        setTimeout(() => {
          if (window.confirm('Would you like to reload the page to refresh all data?')) {
            window.location.reload();
          }
        }, 1000);
      } else {
        setRestoreError(response.data.message || 'Failed to restore backup');
      }
    } catch (err: any) {
      console.error('Restore error:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to restore backup';
      setRestoreError(errorMessage);
    } finally {
      setRestoreLoading(false);
    }
  };

  // Handle backup download
  const handleDownloadBackup = async (backupId: number) => {
    try {
      await backupService.download(backupId);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to download backup');
    }
  };

  return (
    <div className="database-backups-container">
      {/* Header with Stats */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <span className="title-icon">💾</span>
            Database Backups
          </h1>
          <p className="page-subtitle">Manage and restore your database backups</p>
        </div>
        <div className="backup-stats">
          <div className="stat-card">
            <div className="stat-value">{pagination.total}</div>
            <div className="stat-label">Total Backups</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{backups.filter(b => b.is_uploaded_to_onedrive).length}</div>
            <div className="stat-label">☁️ Cloud</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{backups.filter(b => !b.is_uploaded_to_onedrive).length}</div>
            <div className="stat-label">💾 Local</div>
          </div>
        </div>
      </div>
      
      {/* Create Backup Section - Compact */}
      <div className="create-backup-section">
        <div className="compact-form">
          <div className="form-title">
            <span className="section-icon">➕</span>
            <strong>Create New Backup</strong>
          </div>
          <input
            type="text"
            className="compact-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
          />
          <button 
            className="compact-button"
            onClick={handleCreateBackup}
            disabled={createBackupLoading}
          >
            {createBackupLoading ? 'Creating...' : 'Create Backup'}
          </button>
        </div>
      </div>
      
      {/* Error Display */}
      {error && <div className="error-message">{error}</div>}
      
      {/* Backups Section with Integrated Filter */}
      <div className="backups-section">
        <div className="section-header-with-filter">
          <h2>
            <span className="section-icon">📋</span>
            Backup History
          </h2>
          <form onSubmit={handleFilterChange} className="inline-filter">
            <input
              type="date"
              className="compact-date-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="Start Date"
            />
            <input
              type="date"
              className="compact-date-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="End Date"
            />
            <button type="submit" className="compact-filter-button">
              Filter
            </button>
            <button 
              type="button" 
              className="compact-reset-button"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              Reset
            </button>
          </form>
        </div>
        {loading ? (
          <div className="loading-indicator">
            <div className="spinner-large"></div>
            <p>Loading backups...</p>
          </div>
        ) : backups.length === 0 ? (
          <div className="no-backups">
            <div className="empty-state-icon">📦</div>
            <h3>No backups found</h3>
            <p>Create your first backup to get started</p>
          </div>
        ) : (
          <div className="backups-grid">
            {backups.map((backup) => (
              <div key={backup.id} className="backup-card">
                <div className="backup-card-header">
                  <div className="backup-info">
                    <h3 className="backup-filename">{backup.filename}</h3>
                    <div className="backup-meta">
                      <span className="meta-item">
                        <span className="meta-icon">📅</span>
                        {formatDate(backup.backup_datetime)}
                      </span>
                      <span className="meta-item">
                        <span className="meta-icon">👤</span>
                        {backup.created_by_name}
                      </span>
                    </div>
                  </div>
                  <div className="backup-badges">
                    {backup.is_uploaded_to_onedrive ? (
                      <span className="badge badge-cloud" title="Backed up to OneDrive">
                        ☁️ Cloud
                      </span>
                    ) : (
                      <span className="badge badge-local" title="Local storage only">
                        💾 Local
                      </span>
                    )}
                    <span className={`badge badge-${backup.is_auto_backup ? 'auto' : 'manual'}`}>
                      {backup.is_auto_backup ? '🤖 Auto' : '👆 Manual'}
                    </span>
                  </div>
                </div>
                
                <div className="backup-card-body">
                  <div className="backup-details">
                    <div className="detail-item">
                      <span className="detail-label">Size</span>
                      <span className="detail-value">{formatFileSize(backup.file_size_bytes)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className={`detail-value status-${backup.status}`}>
                        {backup.status === 'completed' ? '✓ Completed' : backup.status}
                      </span>
                    </div>
                  </div>
                  
                  {backup.notes && (
                    <div className="backup-notes">
                      <span className="notes-icon">📝</span>
                      <span className="notes-text">{backup.notes}</span>
                    </div>
                  )}
                  
                  {backup.restore_datetime && (
                    <div className="restore-info-card">
                      <span className="restore-icon">↩️</span>
                      <div>
                        <div className="restore-text">Restored on {formatDate(backup.restore_datetime)}</div>
                        <div className="restore-by">By {backup.restored_by_name}</div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="backup-card-actions">
                  <button
                    className="action-button action-download"
                    onClick={() => handleDownloadBackup(backup.id)}
                    title="Download backup file"
                  >
                    <span className="button-icon">⬇️</span>
                    Download
                  </button>
                  <button
                    className="action-button action-restore"
                    onClick={() => openRestoreModal(backup.id)}
                    title="Restore from this backup"
                  >
                    <span className="button-icon">↩️</span>
                    Restore
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {!loading && pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
            className="pagination-button"
          >
            &laquo;
          </button>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="pagination-button"
          >
            &lt;
          </button>
          
          <span className="page-info">
            Page {pagination.page} of {pagination.pages}
          </span>
          
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.pages}
            className="pagination-button"
          >
            &gt;
          </button>
          <button
            onClick={() => handlePageChange(pagination.pages)}
            disabled={pagination.page === pagination.pages}
            className="pagination-button"
          >
            &raquo;
          </button>
        </div>
      )}
      
      {/* Restore Confirmation Modal */}
      {confirmationModalOpen && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <h2>Confirm Database Restore</h2>
            <p className="warning-text">
              Warning: This will replace the current database with the selected backup.
              All changes made after this backup was created will be lost.
            </p>
            <p>
              To proceed, please type <strong>"Confirm"</strong> below:
            </p>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              className="confirmation-input"
              placeholder="Type 'Confirm' to proceed"
            />
            
            <div className="override-backup-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={overrideBackupTable}
                  onChange={(e) => setOverrideBackupTable(e.target.checked)}
                  className="checkbox-input"
                />
                <span className="checkbox-text">
                  <strong>Override backup history table</strong>
                </span>
              </label>
              <div className="override-info">
                {overrideBackupTable ? (
                  <div className="override-warning">
                    ⚠️ <strong>Warning:</strong> This will replace your current backup history with the backup history from the restored database. 
                    Any backups created after this backup was made will be lost from the history. 
                    Local backup files will remain on disk but won't be listed unless you re-import them.
                  </div>
                ) : (
                  <div className="override-note">
                    ℹ️ <strong>Recommended:</strong> Keep this unchecked to preserve your current backup history. 
                    Only check this when migrating to a new server or if you want to restore the exact backup state from that time.
                  </div>
                )}
              </div>
            </div>
            
            {restoreError && <div className="error-message">{restoreError}</div>}
            <div className="modal-buttons">
              <button
                className="cancel-button"
                onClick={() => setConfirmationModalOpen(false)}
                disabled={restoreLoading}
              >
                Cancel
              </button>
              <button
                className="restore-confirm-button"
                onClick={handleRestoreBackup}
                disabled={restoreLoading}
              >
                {restoreLoading ? 'Restoring...' : 'Restore Database'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseBackups;
